import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiHome, FiCalendar, FiBarChart2, FiSettings, FiLogOut, FiMenu, FiX, FiTruck, FiUsers } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { auth } from "../../config/supabase";

// Helper function for active link styles
const getLinkStyles = (isActive) => {
  return isActive
    ? "flex items-center py-2.5 px-4 rounded-lg text-white bg-white/20 font-medium"
    : "flex items-center py-2.5 px-4 rounded-lg text-gray-300 hover:bg-white/10 hover:text-white transition-colors";
};

// Admin Layout Component
const AdminLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname.split('/').pop();
  
  // Add error handling to prevent refreshes
  useEffect(() => {
    const handleError = (e) => {
      console.log('Error intercepted by AdminLayout:', e.message);
      e.stopPropagation();
      e.preventDefault();
      return true;
    };
    
    window.addEventListener('error', handleError, true);
    
    // Set a flag to identify this component is mounted
    window.adminLayoutMounted = true;
    
    // Disable React's error overlay
    if (typeof window.__REACT_ERROR_OVERLAY__ !== 'undefined') {
      window.__REACT_ERROR_OVERLAY__.handleRuntimeError = () => {};
      window.__REACT_ERROR_OVERLAY__.startReportingRuntimeErrors = () => {};
      window.__REACT_ERROR_OVERLAY__.dismissRuntimeErrors = () => {};
    }
    
    // Create an intercept for unhandled promise rejections
    const rejectionHandler = (event) => {
      console.log('Promise rejection intercepted:', event.reason);
      event.preventDefault();
    };
    
    window.addEventListener('unhandledrejection', rejectionHandler);
    
    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', rejectionHandler);
      window.adminLayoutMounted = false;
    };
  }, []);

  const handleLogout = async () => {
    try {
      // Sign out using Supabase Auth
      await auth.signOut();
      
      // Show success message
      toast.success('Logged out successfully');
      
      // Redirect to login page
      navigate('/admin-login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Error logging out');
    }
  };

  return (
    <div className="flex min-h-screen bg-black text-white">
      {/* Mobile Sidebar Toggle */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white text-black rounded-lg"
      >
        {isSidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
      </button>
      
      {/* Sidebar */}
      <aside 
        className={`
          bg-[#0a0a0a] 
          w-64 
          min-h-screen 
          fixed 
          md:static 
          z-40 
          border-r 
          border-white/20
          transform 
          transition-transform 
          duration-300 
          ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        <div className="p-6">
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-yellow-600 mb-8">
            LASMARI ADMIN
          </h1>
          
          <nav className="mt-8 space-y-2">
            <Link
              to="/admin-dashboard/overview"
              className={getLinkStyles(currentPath === 'overview')}
            >
              <FiHome className="mr-3" />
              <span>Dashboard</span>
            </Link>
            
            <Link
              to="/admin-dashboard/bookings"
              className={getLinkStyles(currentPath === 'bookings')}
            >
              <FiCalendar className="mr-3" />
              <span>Bookings</span>
            </Link>
            
            <Link
              to="/admin-dashboard/analytics"
              className={getLinkStyles(currentPath === 'analytics')}
            >
              <FiBarChart2 className="mr-3" />
              <span>Analytics</span>
            </Link>
            
            <Link
              to="/admin-dashboard/fleet"
              className={getLinkStyles(currentPath === 'fleet')}
            >
              <FiTruck className="mr-3" />
              <span>Fleet</span>
            </Link>
            
            <Link
              to="/admin-dashboard/users"
              className={getLinkStyles(currentPath === 'users')}
            >
              <FiUsers className="mr-3" />
              <span>User Management</span>
            </Link>
            
            <Link
              to="/admin-dashboard/settings"
              className={getLinkStyles(currentPath === 'settings')}
            >
              <FiSettings className="mr-3" />
              <span>Settings</span>
            </Link>
          </nav>
          
          <div className="absolute bottom-8 left-0 right-0 px-6">
            <button
              onClick={handleLogout}
              className="flex items-center py-2.5 px-4 w-full text-red-400 hover:bg-red-900/10 rounded-lg transition-colors"
            >
              <FiLogOut className="mr-3" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>
      
      {/* Main Content Area */}
      <main className="flex-1 p-6 md:p-8 overflow-y-auto">
        <div className="pb-16">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout; 